import clearStream from '@anm/helpers/media/clearStream';
import mediaManager from '@anm/helpers/media/mediaManager';
import mergeStateWrapper from '@anm/helpers/redux/mergeStateWrapper';
import useTrackEnded from '@anm/hooks/media/useTrackEnded';
import { useCallback, useEffect, useRef, useState } from 'react';

import useMediaManagerOptions from '../useMediaManagerOptions';

import useStreamCreator from './useStreamCreator';

export type MediaIds = {
  micId: string;
  webcamId: string;
  speakerId: string;
};

const useMediaStreams = () => {
  const [mediaIds, setMediaIds] = useState<Partial<MediaIds>>();
  const prevMediaIdsRef = useRef<MediaIds>({ micId: '', webcamId: '', speakerId: '' });
  const updateMediaIds = mergeStateWrapper(setMediaIds);

  const micOption = useMediaManagerOptions(mediaManager().mics);
  const camOption = useMediaManagerOptions(mediaManager().webcams);

  const [
    { audioStream, videoStream, errors },
    { createStream, setAudioStream, setVideoStream, setErrors }
  ] = useStreamCreator();

  const muteUnmuteMic = useCallback(async () => {
    if (audioStream?.active) {
      audioStream.getAudioTracks()[0].stop();
      setAudioStream(undefined);
      mediaManager().mics.disable();
    } else {
      await createStream({ micId: mediaIds?.micId });
      mediaManager().mics.enable();
    }
  }, [audioStream?.id, mediaIds?.micId]);

  const onOffCamera = useCallback(async () => {
    if (videoStream?.active) {
      videoStream.getVideoTracks()[0].stop();
      setVideoStream(null);
      mediaManager().webcams.disable();
    } else {
      await createStream({ webcamId: mediaIds?.webcamId });
      mediaManager().webcams.enable();
    }
  }, [videoStream?.id, mediaIds?.webcamId]);

  useEffect(() => {
    if (!mediaIds?.micId) return;
    const { micId } = mediaIds;

    micId && prevMediaIdsRef.current.micId !== micId && mediaManager().mics.setCurrentDevice(micId);
    micOption.enable && createStream({ micId });

    prevMediaIdsRef.current.micId = micId;
  }, [mediaIds?.micId, micOption.enable]);

  useEffect(() => {
    if (!mediaIds?.webcamId) return;
    const { webcamId } = mediaIds;

    webcamId && prevMediaIdsRef.current.webcamId !== webcamId && mediaManager().webcams.setCurrentDevice(webcamId);
    camOption.enable && createStream({ webcamId });

    prevMediaIdsRef.current.webcamId = webcamId;
  }, [mediaIds?.webcamId, camOption.resolution, camOption.enable]);

  useEffect(() => {
    if (!mediaIds?.speakerId) return;
    mediaManager().speakers.setCurrentDevice(mediaIds.speakerId);
  }, [mediaIds?.speakerId]);

  useTrackEnded(videoStream, () => {
    setVideoStream(null);
    clearStream(videoStream);
    updateMediaIds({ webcamId: undefined });
    setErrors(prev => ({ ...prev, video: 'NotAllowedError' }));
  });
  useTrackEnded(audioStream, () => {
    setAudioStream(null);
    clearStream(audioStream);
    updateMediaIds({ micId: undefined });
    setErrors(prev => ({ ...prev, audio: 'NotAllowedError' }));
  });

  return [
    { audioStream, videoStream, errors },
    { muteUnmuteMic, onOffCamera, createStreams: updateMediaIds }
  ] as const;
};

export default useMediaStreams;
